import { JobSpecInternalAccessIsolation } from "@eai-toolkit/client";
import { Alert, MenuItem, styled, TextField } from "@mui/material";
import React, { useCallback } from "react";

import { useAppSelector } from "../../../store/hooks";
import { isReplicaJob } from "../utils";
import { wideFieldStyle } from "./NewJobFormLayout";

const IsolationField = styled(TextField)(({ theme }) => ({
  ...wideFieldStyle,
  paddingBottom: theme.spacing(2),
}));

const ApiValue = styled("code")({
  fontWeight: "bold",
});

const IsolationWarning = styled(Alert)(({ theme }) => ({
  ...wideFieldStyle,
  marginBottom: theme.spacing(2),
}));

type LabelContext = {
  isReplica: boolean;
  userName: string | undefined;
  /** Undefined until an account is selected. */
  accountName: string | undefined;
  orgName: string | undefined;
};

const withName = (name: string | undefined) => (name ? ` (${name})` : "");

// The API value is shown as bold code so CLI users can match each option to its
// `--internal-access-isolation` value. Keyed by the client's enum so
// if the backend adds/removes/changes any values, the build will fail
// until this is updated.
const OPTION_LABELS: Record<
  JobSpecInternalAccessIsolation,
  (context: LabelContext) => React.ReactNode
> = {
  none: ({ isReplica }) => (
    <>
      <ApiValue>none</ApiValue> except this job itself
      {isReplica && " and its replicas"}
    </>
  ),
  user: ({ userName }) => (
    <>
      jobs submitted by the same <ApiValue>user</ApiValue>
      {withName(userName)}
    </>
  ),
  account: ({ accountName }) => (
    <>
      jobs submitted in the same <ApiValue>account</ApiValue>
      {withName(accountName)}
    </>
  ),
  organization: ({ orgName }) => (
    <>
      jobs submitted in the same <ApiValue>organization</ApiValue>
      {withName(orgName)}
    </>
  ),
};

const KNOWN_OPTIONS: readonly string[] = Object.values(
  JobSpecInternalAccessIsolation,
);

const isKnownOption = (
  option: string,
): option is JobSpecInternalAccessIsolation => KNOWN_OPTIONS.includes(option);

// A job copied as a template can carry a value this build doesn't know (e.g.
// the backend added one before the client was regenerated). It is still sent
// on submit, so show it as its own option instead of as a blank select.
const unknownOptionLabel = (option: string) => <ApiValue>{option}</ApiValue>;

type Props = {
  value: JobSpecInternalAccessIsolation;
  onUpdate: (value: JobSpecInternalAccessIsolation) => void;
  /** Full name of the account selected for the job (e.g. `snow.team.acct`),
   * or null before one is selected. */
  accountFullName: string | null;
  /** Replica count from `jobSpec.resources.replicas`. */
  replicas: number | undefined;
  /** Whether the job has an internal DNS name set. */
  hasDnsName: boolean;
};

function InternalAccessIsolationField({
  value,
  onUpdate,
  accountFullName,
  replicas,
  hasDnsName,
}: Props) {
  // The job's creator is whoever submits this form.
  const userName = useAppSelector((state) => state.auth.user?.fullName);
  const isReplica = isReplicaJob(replicas);
  // Not a validation error: the user may have a reason, so they can still
  // submit. Replicas are excluded because "none" still lets them reach each
  // other. Mirrors the CLI's warning for the same combination.
  const showUnreachableWarning = hasDnsName && value === "none" && !isReplica;
  const context: LabelContext = {
    isReplica,
    userName,
    accountName: accountFullName ?? undefined,
    // Org name is the first part of the account name, separated by a dot
    // (eg: snow.home --> snow). The account's `organization` field is an ID,
    // not a display name, so it is better to extract the org name like this.
    orgName: accountFullName?.includes(".")
      ? accountFullName.split(".")[0]
      : undefined,
  };

  const options = KNOWN_OPTIONS.includes(value)
    ? KNOWN_OPTIONS
    : [...KNOWN_OPTIONS, value];

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(e.target.value as JobSpecInternalAccessIsolation);
    },
    [onUpdate],
  );

  return (
    <>
      <IsolationField
        variant="outlined"
        fullWidth
        select
        label="What jobs can reach this job"
        value={value}
        onChange={handleChange}
        helperText="Which other jobs can reach this job via internal DNS (name or IP)."
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            <span>
              {isKnownOption(option)
                ? OPTION_LABELS[option](context)
                : unknownOptionLabel(option)}
            </span>
          </MenuItem>
        ))}
      </IsolationField>
      {showUnreachableWarning && (
        <IsolationWarning severity="warning">
          Internal DNS is set but isolation is <ApiValue>none</ApiValue>, so
          other jobs won&apos;t be able to reach this job.
        </IsolationWarning>
      )}
    </>
  );
}

export default React.memo(InternalAccessIsolationField);
